import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
  Box,
  TextField,
  CircularProgress,
  Tabs,
  Tab,
  Divider,
  Paper,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Palette as PaletteIcon,
  FormatColorFill as FillIcon,
  TextFields as TextIcon,
  Add as AddIcon,
  Check as CheckIcon,
  Delete as DeleteIcon,
  Refresh as ResetIcon,
  Save as SaveIcon
} from '@mui/icons-material';

// Predefined color schemes
const colorSchemePresets = {
  default: {
    primary: '#1976d2',
    secondary: '#dc004e',
    text: '#333333',
    background: '#ffffff',
    surface: '#f5f5f5',
    border: '#e0e0e0',
    success: '#4caf50',
    error: '#f44336',
    warning: '#ff9800',
    info: '#2196f3'
  },
  dark: {
    primary: '#90caf9',
    secondary: '#f48fb1',
    text: '#ffffff',
    background: '#121212',
    surface: '#1e1e1e',
    border: '#333333',
    success: '#81c784',
    error: '#e57373',
    warning: '#ffb74d',
    info: '#64b5f6'
  },
  pastel: {
    primary: '#88c9ff',
    secondary: '#f6a5c1',
    text: '#4a4a4a',
    background: '#f8f9fa',
    surface: '#ffffff',
    border: '#e9ecef',
    success: '#a8e6cf',
    error: '#ffaaa5',
    warning: '#ffd3b6',
    info: '#a2d2ff'
  },
  nature: {
    primary: '#2e7d32',
    secondary: '#ff6d00',
    text: '#3e2723',
    background: '#f1f8e9',
    surface: '#ffffff',
    border: '#c5e1a5',
    success: '#558b2f',
    error: '#d84315',
    warning: '#ef6c00',
    info: '#1b5e20'
  },
  corporate: {
    primary: '#0d47a1',
    secondary: '#455a64',
    text: '#212121',
    background: '#ffffff',
    surface: '#fafafa',
    border: '#eeeeee',
    success: '#1b5e20',
    error: '#b71c1c',
    warning: '#e65100',
    info: '#01579b'
  }
};

/**
 * Color Scheme Editor component
 * Dialog for customizing the color scheme of wireframes/designs
 */
const ColorSchemeEditor = ({ 
  open, 
  onClose, 
  onApply, 
  initialColors = null,
  isSaving = false 
}) => {
  // Initialize with default colors if none provided
  const [colorScheme, setColorScheme] = useState(
    initialColors || colorSchemePresets.default
  );
  const [selectedPreset, setSelectedPreset] = useState('custom');
  const [activeTab, setActiveTab] = useState(0);
  const [previewHtml, setPreviewHtml] = useState('');

  // Update colors when initialColors changes
  useEffect(() => {
    if (initialColors) {
      setColorScheme(initialColors);
      
      // Check if it matches any preset
      const matchingPreset = Object.entries(colorSchemePresets).find(([name, colors]) => {
        return JSON.stringify(colors) === JSON.stringify(initialColors);
      });
      
      setSelectedPreset(matchingPreset ? matchingPreset[0] : 'custom');
    }
  }, [initialColors, open]);

  // Generate preview HTML when color scheme changes
  useEffect(() => {
    generatePreviewHtml();
  }, [colorScheme]);

  // Generate sample HTML for preview
  const generatePreviewHtml = () => {
    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <style>
          body {
            margin: 0;
            padding: 16px;
            font-family: Arial, sans-serif;
            background-color: ${colorScheme.background};
            color: ${colorScheme.text};
          }
          .container {
            border: 1px solid ${colorScheme.border};
            border-radius: 4px;
            padding: 16px;
            background-color: ${colorScheme.surface};
            max-width: 600px;
            margin: 0 auto;
          }
          .header {
            margin-bottom: 16px;
            border-bottom: 1px solid ${colorScheme.border};
            padding-bottom: 8px;
          }
          .button-primary {
            background-color: ${colorScheme.primary};
            color: white;
            border: none;
            padding: 8px 16px;
            border-radius: 4px;
            cursor: pointer;
          }
          .button-secondary {
            background-color: ${colorScheme.secondary};
            color: white;
            border: none;
            padding: 8px 16px;
            border-radius: 4px;
            cursor: pointer;
          }
          .info {
            background-color: ${colorScheme.info};
            color: white;
            padding: 8px;
            border-radius: 4px;
            margin-top: 16px;
          }
          .success {
            background-color: ${colorScheme.success};
            color: white;
            padding: 8px;
            border-radius: 4px;
            margin-top: 8px;
          }
          .error {
            background-color: ${colorScheme.error};
            color: white;
            padding: 8px;
            border-radius: 4px;
            margin-top: 8px;
          }
          .warning {
            background-color: ${colorScheme.warning};
            color: white;
            padding: 8px;
            border-radius: 4px;
            margin-top: 8px;
          }
        </style>
      </head>
      <body>
        <div class="container">
          <div class="header">
            <h2>Color Scheme Preview</h2>
          </div>
          <div>
            <p>This preview shows how your color scheme will look in the design.</p>
            <div>
              <button class="button-primary">Primary Button</button>
              <button class="button-secondary">Secondary Button</button>
            </div>
            <div class="info">This is an info notification</div>
            <div class="success">This is a success notification</div>
            <div class="error">This is an error notification</div>
            <div class="warning">This is a warning notification</div>
          </div>
        </div>
      </body>
      </html>
    `;
    
    setPreviewHtml(html);
  };

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Handle color change for a specific key
  const handleColorChange = (key, value) => {
    setColorScheme(prev => ({
      ...prev,
      [key]: value
    }));
    setSelectedPreset('custom');
  };

  // Handle preset selection
  const handlePresetChange = (preset) => {
    setColorScheme(colorSchemePresets[preset]);
    setSelectedPreset(preset);
  };

  // Reset to initial colors
  const handleReset = () => {
    setColorScheme(initialColors || colorSchemePresets.default);
    if (initialColors) {
      // Check if it matches any preset
      const matchingPreset = Object.entries(colorSchemePresets).find(([name, colors]) => {
        return JSON.stringify(colors) === JSON.stringify(initialColors);
      });
      
      setSelectedPreset(matchingPreset ? matchingPreset[0] : 'custom');
    } else {
      setSelectedPreset('default');
    }
  };

  // Apply color scheme
  const handleApply = () => {
    if (onApply) {
      onApply(colorScheme);
    }
  };

  // Render the preset selector
  const renderPresetSelector = () => (
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle2" gutterBottom>
        Color Presets
      </Typography>
      <Grid container spacing={1}>
        {Object.entries(colorSchemePresets).map(([name, preset]) => (
          <Grid item key={name}>
            <Tooltip title={name.charAt(0).toUpperCase() + name.slice(1)}>
              <IconButton
                onClick={() => handlePresetChange(name)}
                sx={{
                  width: 40,
                  height: 40,
                  backgroundColor: preset.primary,
                  border: `2px solid ${selectedPreset === name ? '#000' : 'transparent'}`,
                  '&:hover': {
                    backgroundColor: preset.primary,
                    opacity: 0.9
                  },
                  position: 'relative'
                }}
              >
                {selectedPreset === name && (
                  <CheckIcon sx={{ color: '#fff' }} fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
          </Grid>
        ))}
      </Grid>
    </Box>
  );

  // Render the color editor fields
  const renderColorEditor = () => {
    // Group colors by category
    const mainColors = ['primary', 'secondary', 'text', 'background', 'surface'];
    const accentColors = ['success', 'error', 'warning', 'info', 'border'];
    
    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          Main Colors
        </Typography>
        <Grid container spacing={2}>
          {mainColors.map(key => (
            <Grid item xs={6} sm={4} key={key}>
              <TextField
                fullWidth
                label={key.charAt(0).toUpperCase() + key.slice(1)}
                value={colorScheme[key] || ''}
                onChange={(e) => handleColorChange(key, e.target.value)}
                InputProps={{
                  startAdornment: (
                    <Box
                      sx={{
                        width: 20,
                        height: 20,
                        borderRadius: 1,
                        backgroundColor: colorScheme[key] || '#ccc',
                        mr: 1,
                        border: '1px solid #ccc'
                      }}
                    />
                  ),
                }}
                size="small"
              />
            </Grid>
          ))}
        </Grid>
        
        <Typography variant="subtitle2" gutterBottom sx={{ mt: 3 }}>
          Accent Colors
        </Typography>
        <Grid container spacing={2}>
          {accentColors.map(key => (
            <Grid item xs={6} sm={4} key={key}>
              <TextField
                fullWidth
                label={key.charAt(0).toUpperCase() + key.slice(1)}
                value={colorScheme[key] || ''}
                onChange={(e) => handleColorChange(key, e.target.value)}
                InputProps={{
                  startAdornment: (
                    <Box
                      sx={{
                        width: 20,
                        height: 20,
                        borderRadius: 1,
                        backgroundColor: colorScheme[key] || '#ccc',
                        mr: 1,
                        border: '1px solid #ccc'
                      }}
                    />
                  ),
                }}
                size="small"
              />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  };

  // Render the color scheme preview
  const renderPreview = () => (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle2" gutterBottom>
        Preview
      </Typography>
      <Paper elevation={0} variant="outlined">
        <Box sx={{ height: 300, overflowY: 'auto' }}>
          <iframe
            srcDoc={previewHtml}
            title="Color Scheme Preview"
            style={{
              width: '100%',
              height: '100%',
              border: 'none'
            }}
          />
        </Box>
      </Paper>
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        Customize Color Scheme
        {isSaving && <CircularProgress size={24} sx={{ ml: 2 }} />}
      </DialogTitle>
      
      <DialogContent>
        <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 2 }}>
          <Tab 
            icon={<PaletteIcon fontSize="small" />} 
            iconPosition="start"
            label="Presets" 
          />
          <Tab 
            icon={<FillIcon fontSize="small" />} 
            iconPosition="start"
            label="Custom Colors" 
          />
          <Tab 
            icon={<TextIcon fontSize="small" />} 
            iconPosition="start"
            label="Preview" 
          />
        </Tabs>
        
        <Divider sx={{ mb: 2 }} />
        
        {activeTab === 0 && renderPresetSelector()}
        {activeTab === 1 && renderColorEditor()}
        {activeTab === 2 && renderPreview()}
      </DialogContent>
      
      <DialogActions>
        <Button 
          startIcon={<ResetIcon />}
          onClick={handleReset}
          color="inherit"
          disabled={isSaving}
        >
          Reset
        </Button>
        <Button 
          onClick={onClose}
          color="inherit"
          disabled={isSaving}
        >
          Cancel
        </Button>
        <Button 
          startIcon={<SaveIcon />}
          onClick={handleApply}
          variant="contained"
          color="primary"
          disabled={isSaving}
        >
          Apply Colors
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ColorSchemeEditor;